import React, { useState } from 'react';
import { Result } from '../../types';
import { Table } from '../ui';
import { formatMargin } from '../../utils';

interface ResultsTableProps {
  results: Result[] & { pick_type?: string; pick_order?: number }[];
  className?: string;
}

type SortField = 'position' | 'horse_name' | 'jockey' | 'price_sp' | 'prizemoney_won' | 'margin' | 'pick_type';
type SortDirection = 'asc' | 'desc';

type HorseStatus = 'finished' | 'pending' | 'scratched' | 'no_result';

export const ResultsTable: React.FC<ResultsTableProps> = ({ results, className = '' }) => {
  const [sortField, setSortField] = useState<SortField>('position');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Detect partial results: some positions are filled but not all horses have SP data yet
  const hasAnyPositions = results.some(r => r.position !== undefined && r.position > 0);
  const hasAllSp = results.length > 0 && results.every(r => r.price_sp);
  const isPartialResults = hasAnyPositions && !hasAllSp;

  const getHorseStatus = (result: Result & { pick_type?: string; pick_order?: number }): HorseStatus => {
    if (result.scratched) return 'scratched';
    if (result.position !== undefined && result.position > 0) return 'finished';
    if (isPartialResults) return 'pending';
    return 'no_result';
  };

  // Handle sort
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort results
  const sortedResults = [...results].sort((a, b) => {
    // Special handling for position field to move scratched/undefined positions to bottom
    if (sortField === 'position') {
      // Order: finished → pending → no_result → scratched
      const statusOrder: Record<HorseStatus, number> = { finished: 0, pending: 1, no_result: 2, scratched: 3 };
      const aStatus = getHorseStatus(a);
      const bStatus = getHorseStatus(b);

      if (aStatus === 'finished' && bStatus === 'finished') {
        return sortDirection === 'asc'
          ? a.position! - b.position!
          : b.position! - a.position!;
      }

      if (aStatus !== bStatus) {
        return statusOrder[aStatus] - statusOrder[bStatus];
      }

      // Same status group — sort by horse name
      return a.horse_name?.localeCompare(b.horse_name || '') || 0;
    }
    
    // Special handling for pick_type field
    if (sortField === 'pick_type') {
      // Define pick type order: top picks (by order), then value picks, then no picks
      const getPickTypeValue = (result: any) => {
        if (!result.pick_type) return 999; // No pick comes last
        if (result.pick_type === 'top') return result.pick_order || 1; // Top picks ordered by pick_order
        if (result.pick_type === 'value') return 100; // Value picks come after top picks
        return 500; // Other pick types in the middle
      };
      
      const aValue = getPickTypeValue(a);
      const bValue = getPickTypeValue(b);
      
      return sortDirection === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
    
    // For other fields, use standard sorting
    let aValue: any = a[sortField as keyof Result];
    let bValue: any = b[sortField as keyof Result];
    
    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    // Handle undefined values
    if (aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
    if (bValue === undefined) return sortDirection === 'asc' ? -1 : 1;
    
    // Compare values
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Get position circle color
  const getPositionCircleColor = (position: number | undefined) => {
    if (position === undefined) return 'bg-gray-300';
    
    switch (position) {
      case 1:
        return 'bg-green-500';
      case 2:
        return 'bg-blue-500';
      case 3:
        return 'bg-blue-400';
      case 4:
        return 'bg-gray-500';
      case 5:
        return 'bg-gray-400';
      case 6:
        return 'bg-gray-400';
      case 7:
        return 'bg-gray-400';
      case 8:
        return 'bg-gray-400';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <div className={className}>
      <Table striped hoverable className="compact-table">
        <Table.Head>
          <tr>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'position' ? sortDirection : null}
              onSort={() => handleSort('position')}
              className="w-16"
            >
              Pos
            </Table.HeaderCell>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'horse_name' ? sortDirection : null}
              onSort={() => handleSort('horse_name')}
            >
              Horse
            </Table.HeaderCell>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'pick_type' ? sortDirection : null}
              onSort={() => handleSort('pick_type')}
              className="w-24"
            >
              Pick Type
            </Table.HeaderCell>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'jockey' ? sortDirection : null}
              onSort={() => handleSort('jockey')}
              className="hide-on-mobile"
            >
              Jockey
            </Table.HeaderCell>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'price_sp' ? sortDirection : null}
              onSort={() => handleSort('price_sp')}
            >
              SP / Place
            </Table.HeaderCell>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'prizemoney_won' ? sortDirection : null}
              onSort={() => handleSort('prizemoney_won')}
            >
              Win
            </Table.HeaderCell>
            <Table.HeaderCell 
              sortable 
              sorted={sortField === 'margin' ? sortDirection : null}
              onSort={() => handleSort('margin')}
              className="hide-on-mobile"
            >
              Margin
            </Table.HeaderCell>
          </tr>
        </Table.Head>
        <Table.Body>
          {sortedResults.map((result, index) => (
            <Table.Row 
              key={index} 
              isEven={index % 2 === 1} 
              hoverable
              className={result.pick_type === 'top' && result.pick_order === 1 && result.position === 1 ? 'bg-green-100' : ''}
            >
              <Table.Cell>
                {(() => {
                  const status = getHorseStatus(result);
                  if (status === 'finished') {
                    return (
                      <div className={`${getPositionCircleColor(result.position)} text-white rounded-full w-8 h-8 flex items-center justify-center font-bold`}>
                        {result.position}
                      </div>
                    );
                  }
                  if (status === 'pending') {
                    return (
                      <div className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-xs">
                        TBC
                      </div>
                    );
                  }
                  if (status === 'scratched') {
                    return (
                      <div className="bg-gray-200 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center font-bold text-xs">
                        SCR
                      </div>
                    );
                  }
                  return (
                    <div className="bg-gray-200 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center font-bold text-xs">
                      DNF
                    </div>
                  );
                })()}
              </Table.Cell>
              <Table.Cell className="font-medium horse-name-cell">
                {result.horse_number && <span className="mr-2">{result.horse_number}</span>}
                <span className={getHorseStatus(result) === 'scratched' ? 'line-through text-gray-400' : ''}>
                  {result.horse_name}
                </span>
                {getHorseStatus(result) === 'scratched' && (
                  <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                    Scratched
                  </span>
                )}
                {getHorseStatus(result) === 'pending' && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                    Pending
                  </span>
                )}
              </Table.Cell>
              <Table.Cell>
                {result.pick_type === 'top' && (
                  <span className="bg-deep-navy text-white px-2 py-0.5 rounded text-xs">
                    Top {result.pick_order}
                  </span>
                )}
                {result.pick_type === 'value' && (
                  <span className="bg-racing-green text-white px-2 py-0.5 rounded text-xs">
                    Value
                  </span>
                )}
              </Table.Cell>
              <Table.Cell className="hide-on-mobile">{result.jockey}</Table.Cell>
              <Table.Cell>
                {result.price_sp ? `$${result.price_sp.toFixed(2)}` : '-'}
                {result.price_place ? ` / $${result.price_place.toFixed(2)}` : ''}
              </Table.Cell>
              <Table.Cell>{result.prizemoney_won || '-'}</Table.Cell>
              <Table.Cell className="hide-on-mobile">
                {result.position !== undefined && result.position > 0 
                  ? formatMargin(result.margin) 
                  : '-'}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

export default ResultsTable;
